import moment from 'moment';
import {
    contentLinkNodeNavigateTo,
    coursesLinkNodePath,
    externalLinkNodeHandler,
    internalLinkNodeHandler,
    meditationLinkNodePath,
    programLinkNodePath,
} from 'src/modules/dashboard/helpers/contentLinkNodeNavigateTo';
import { ContentCardItemWithId } from '@modernhealth/vital-components';

import i18n from 'src/config/i18n';
import { getCourseHeroImageUrl } from 'src/modules/courses/helpers/getCourseHeroImageUrl';

import {
    ContentLinkFragment,
    ContentLink_CircleTopicLinkNode_Fragment as CircleTopicLinkFragment,
} from '../documents/getHomeScreen.generated';

export function getFirstFacilitatorPhoto(fragment: CircleTopicLinkFragment) {
    return (
        fragment.circleTopic?.sessions.edges
            .flatMap(session => session?.node.facilitators.map(facilitator => facilitator?.photo))
            .find((photo): photo is string => typeof photo === 'string' && !!photo) ?? ''
    );
}

export const mapContentLinkToContentCardProps = (
    link: ContentLinkFragment,
    showCirclesInMemberWeb = false,
): ContentCardItemWithId | null => {
    switch (link.__typename) {
        case 'MeditationLinkNode':
            return link.meditation
                ? {
                      id: link.meditation.uuid,
                      title: link.meditation.name,
                      description: i18n.t(
                          link.mediaType?.name
                              ? `shared:media_type.duration_with_min`
                              : 'dashboard:meditation.duration',
                          {
                              duration: link.meditation.duration ?? 1,
                              mediaTypeName: link.mediaType?.name,
                          },
                      ),
                      image: link.meditation.image ?? '',
                      linkTo: meditationLinkNodePath(link),
                      isCompleted: link.meditation.isCompleted ?? false,
                      progressBarValue:
                          link.meditation.isCompleted || !link.meditation.progressPercent
                              ? undefined
                              : link.meditation.progressPercent * 0.01,
                      showNewContentIndicator: link.meditation?.isNew,
                      descriptionStartIconUrl: link.mediaType?.icon || '',
                  }
                : null;
        case 'ProgramLinkNode':
            return link.program
                ? {
                      id: link.program.uuid,
                      title: link.program.name,
                      description: i18n.t(
                          link.mediaType?.name
                              ? `shared:media_type.duration_with_min`
                              : 'dashboard:program.duration',
                          {
                              duration: link.program.duration ?? 1,
                              mediaTypeName: link.mediaType?.name,
                          },
                      ),
                      image: link.program.image ?? '',
                      linkTo: programLinkNodePath(link),
                      isCompleted: link.program.isCompleted ?? false,
                      progressBarValue:
                          link.program.isCompleted || !link.program.progressPercent
                              ? undefined
                              : link.program.progressPercent * 0.01,
                      showNewContentIndicator: link.program?.isNew,
                      descriptionStartIconUrl: link.mediaType?.icon || '',
                  }
                : null;
        case 'CourseLinkNode':
            const mediaTypeName =
                link.mediaType?.name || i18n.t('courses:course.default_media_type');
            let description;

            if (link.course.subtitle) {
                description = `${mediaTypeName} • ${link.course.subtitle}`;
            } else {
                description = i18n.t('shared:media_type.duration_with_min', {
                    duration: link.course.duration ?? 1,
                    mediaTypeName,
                });
            }

            return {
                id: link.id,
                title: link.course.title,
                description,
                linkTo: coursesLinkNodePath(link),
                image: link.course.thumbnailImageUrl
                    ? link.course.thumbnailImageUrl
                    : getCourseHeroImageUrl(link.course) ?? '',
                isCompleted: !!link.course.progress?.completedAt ?? false,
                progressBarValue:
                    !!link.course.progress.completedAt || !link.course.progressPercent
                        ? undefined
                        : link.course.progressPercent * 0.01,
                showNewContentIndicator: link.course?.isNew,
                descriptionStartIconUrl: link.mediaType?.icon || '',
            };
        case 'ExternalLinkNode':
            return {
                id: link.id,
                title: link.title,
                description: link.subtitle ?? '',
                image: link.image ?? '',
                onClick: externalLinkNodeHandler(link),
            };
        case 'InternalLinkNode':
            return {
                id: link.id,
                title: link.title,
                description: link.subtitle ?? '',
                image: link.image ?? '',
                onClick: internalLinkNodeHandler(link),
            };

        case 'CircleSeriesLinkNode':
            return link.circleSeries
                ? {
                      id: link.circleSeries.id,
                      title: link.circleSeries.name,
                      description:
                          link.circleSeries.shortDescription || link.circleSeries.description,
                      image: link.circleSeries.illustration ?? '',
                      ...contentLinkNodeNavigateTo(link, showCirclesInMemberWeb),
                  }
                : null;
        case 'CircleTopicLinkNode':
            const firstSessionStartDate = moment(
                link.circleTopic?.sessions.edges[0]?.node.startTime,
            ).format('M/D, h:mm A');

            const numberOfSessions = link.circleTopic?.sessions.edges.length ?? 0;
            return link.circleTopic
                ? {
                      id: link.circleTopic.id,
                      title: link.circleTopic.name ?? '',
                      description: i18n.t(
                          `dashboard:circle.duration.${link.circleTopic.format.toLowerCase()}`,
                          { duration: link.circleTopic.duration },
                      ),
                      image: getFirstFacilitatorPhoto(link),
                      eyebrowText:
                          numberOfSessions > 1
                              ? i18n.t(`dashboard:circle.more_sessions`, {
                                    date: firstSessionStartDate,
                                    count: numberOfSessions - 1,
                                })
                              : firstSessionStartDate,
                      ...contentLinkNodeNavigateTo(link, showCirclesInMemberWeb),
                  }
                : null;
        default:
            return null;
    }
};
